import { Form } from "antd"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  ActionFunction,
  LinksFunction,
  LoaderFunction,
  MetaFunction,
  useFetcher,
  useSearchParams
} from "react-router"

import AuthLayout from "~/components/auth-layout"
import OtpVerification from "~/containers/auth/otp-verification"
import LoginForm from "~/containers/login/login-form"
import type { LoginFormValues } from "~/containers/login/types"
import { loginAction, loginLoader } from "~/loaders-actions/login"
import { axiosInstance } from "~/utils/axios"

export const links: LinksFunction = () => []

export const meta: MetaFunction = () => [
  { title: "OrbitShift | Login" },
  { name: "description", content: "Sign in to your OrbitShift workspace." }
]

export const action: ActionFunction = loginAction
export const loader: LoaderFunction = loginLoader

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function useBackButtonLock(canStepBack: boolean, onStepBack: () => void) {
  const canStepBackRef = useRef(canStepBack)
  const onStepBackRef = useRef(onStepBack)
  canStepBackRef.current = canStepBack
  onStepBackRef.current = onStepBack

  const handlePopState = useCallback(() => {
    // Keep the user on the login page (never navigate away), but if they are
    // on a later step (connections / password / OTP) let Back undo that step
    // and return to email entry instead of being a dead no-op.
    history.pushState(null, "", window.location.href)
    if (canStepBackRef.current) {
      onStepBackRef.current()
    }
  }, [])

  const handleBeforeUnload = useCallback(() => {
    history.pushState(null, "", window.location.href)
  }, [])

  useEffect(() => {
    history.pushState(null, "", window.location.href)
    window.addEventListener("popstate", handlePopState)
    window.addEventListener("beforeunload", handleBeforeUnload)
    sessionStorage.clear()
    localStorage.removeItem("agent_account")

    return () => {
      window.removeEventListener("popstate", handlePopState)
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [handlePopState, handleBeforeUnload])
}

function useAutoResetFlag(flag: boolean, delayMs: number) {
  const [value, setValue] = useState(flag)

  useEffect(() => {
    if (!value) return
    const timer = setTimeout(() => setValue(false), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])

  return [value, setValue] as const
}

export default function Login() {
  const fetcher = useFetcher()
  const [form] = Form.useForm<LoginFormValues>()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get("redirectTo") || ""
  const rawPrefillEmail = searchParams.get("email") || ""
  const prefillEmail = EMAIL_REGEX.test(rawPrefillEmail) ? rawPrefillEmail : ""
  const credentialsRef = useRef({ email: "", password: "" })
  const [error, setError] = useState<string | null>(null)
  const [generatingSsoLink, setGeneratingSsoLink] = useState({
    type: "",
    loading: false
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isResendOtp, setIsResendOtp] = useAutoResetFlag(false, 5000)
  const [connections, setConnections] = useState<null | { name: string }[]>(
    null
  )

  const isOtpRequestSucess =
    (fetcher.data?.success || fetcher.data?.error) &&
    fetcher.data?.data?.request_otp

  useEffect(() => {
    if (prefillEmail) {
      form.setFieldsValue({ _email: prefillEmail })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillEmail])

  useEffect(() => {
    if (!fetcher.data?.data?.connections) return
    setConnections(fetcher.data?.data?.connections)
  }, [fetcher.data?.data?.connections])

  useEffect(() => {
    return () => {
      credentialsRef.current = { email: "", password: "" }
    }
  }, [])

  const onFinish = useCallback(
    async (values: LoginFormValues) => {
      setIsSubmitting(true)

      if (!connections || connections?.length === 0) {
        await fetcher.submit(
          {
            email: values._email,
            action_type: "get_organisations",
            redirectTo
          },
          { method: "post", action: "/login" }
        )
        credentialsRef.current = {
          email: values._email,
          password: ""
        }
      } else {
        if (!isOtpRequestSucess) {
          form.setFieldsValue({ otp: "" })
          credentialsRef.current = {
            email: values._email,
            password: values._password
          }
        }
        await fetcher.submit(
          {
            email: values._email || credentialsRef.current.email,
            password: values._password || credentialsRef.current.password,
            request_otp: isOtpRequestSucess ? "false" : "true",
            otp: values.otp,
            redirectTo
          },
          { method: "post", action: "/login" }
        )
      }
      setIsSubmitting(false)
    },
    [connections, fetcher, form, isOtpRequestSucess, redirectTo]
  )

  const resendOtp = useCallback(async () => {
    await fetcher.submit(
      {
        email: credentialsRef.current.email,
        password: credentialsRef.current.password,
        request_otp: "true",
        resend_otp: "true",
        redirectTo
      },
      { method: "post", action: "/login" }
    )
    form.setFieldsValue({ otp: "" })
    setIsResendOtp(true)
  }, [fetcher, form, redirectTo, setIsResendOtp])

  const resetForm = useCallback(() => {
    fetcher.submit({ reset_form: "true" }, { method: "post", action: "/login" })
    setConnections(null)
    credentialsRef.current = { email: "", password: "" }
    form.setFieldValue("_password", "")
    form.setFieldValue("otp", "")
  }, [fetcher, form])

  // Browser Back undoes the current login step (connections / password / OTP)
  // and returns to email entry — where the "New to OrbitShift?" sign-up CTA
  // lives — instead of being locked. At email entry it keeps the page locked.
  const canStepBack = !!connections || !!isOtpRequestSucess
  useBackButtonLock(canStepBack, resetForm)

  const onSsoClick = useCallback(async (connection: string) => {
    try {
      setGeneratingSsoLink({ type: connection, loading: true })
      const response = await axiosInstance("sso/login", {
        method: "POST",
        data: {
          connection,
          email: credentialsRef.current.email
        }
      })
      const authUrl = response.data.data?.authUrl
      try {
        const parsed = new URL(authUrl)
        if (!["https:"].includes(parsed.protocol)) {
          throw new Error("Invalid protocol")
        }
        window.location.href = authUrl
      } catch {
        setError("Invalid SSO redirect URL")
        setGeneratingSsoLink({ type: "", loading: false })
      }
    } catch {
      setError(`Failed to connect ${connection} account`)
      setGeneratingSsoLink({ type: "", loading: false })
    }
  }, [])

  const isPending =
    isSubmitting ||
    fetcher.state === "loading" ||
    fetcher.data?.data?.redirectUrl

  const fetcherError = isPending
    ? ""
    : (fetcher.data as { error: string })?.error

  const { filteredConnections, isEmailConnection, noAccountFound } =
    useMemo(() => {
      const filtered = connections?.filter(
        (c: { name: string }) => c.name !== "email"
      )
      const hasEmail = connections?.some(
        (c: { name: string }) => c.name === "email"
      )
      const noAccount =
        !isSubmitting &&
        connections !== null &&
        connections.length === 0 &&
        fetcher.state === "idle"
      return {
        filteredConnections: filtered,
        isEmailConnection: hasEmail,
        noAccountFound: noAccount
      }
    }, [connections, isSubmitting, fetcher.state])

  return (
    <AuthLayout>
      {isOtpRequestSucess ? (
        <OtpVerification
          form={form}
          title="Check your email"
          email={credentialsRef.current.email}
          error={fetcherError || error}
          isPending={isPending}
          showResendSuccess={isResendOtp && !!fetcher.data?.success}
          resendOtp={resendOtp}
          resetForm={resetForm}
          onFinish={onFinish}
        />
      ) : (
        <LoginForm
          form={form}
          connections={connections}
          filteredConnections={filteredConnections}
          isEmailConnection={isEmailConnection}
          noAccountFound={noAccountFound}
          isPending={isPending}
          fetcherError={fetcherError}
          error={error}
          credentialsEmail={credentialsRef.current.email}
          redirectTo={redirectTo}
          generatingSsoLink={generatingSsoLink}
          onFinish={onFinish}
          resetForm={resetForm}
          onSsoClick={onSsoClick}
        />
      )}
    </AuthLayout>
  )
}
